import { GraphQLResult } from '@aws-amplify/api';
import * as R from 'ramda';
import {
  Experience,
  JobMatchesSummaryQuery,
  JobNotMatchedReasonCode,
  Level,
  PositionTitleType,
  PotentialOpenPositionMatchesQuery,
  Role,
} from '../API';
import callGraphQL from '../graphql-wrapper';
import { jobMatchesSummary, potentialOpenPositionMatches } from '../graphql/queries';
import { Maybe } from '../types';
import TechnicalSkill from './technical-skills';

interface Company {
  id: string;
  name: string;
  url?: Maybe<string>;
  logoUrl?: Maybe<string>;
  summary?: Maybe<string>;
}

export interface OpenPosition {
  id: string;
  name: string;
  company: Company;
  role: Role;
  active: boolean;
  experience: Experience;
  skillIds: string[];
}

export interface JobFuzzyMatch {
  fullMatch: boolean;
  level?: Level | null;
  role?: Role | null;
  titleType?: PositionTitleType | null;
  skills?: TechnicalSkill[] | null;
  reasonCode?: JobNotMatchedReasonCode | null;
  missingSkills?: TechnicalSkill[] | null;
}

export const ROLE_HUMAN_NAMES = {
  [Role.BACKEND]: 'Back-end',
  [Role.FRONTEND]: 'Front-end',
  [Role.FULLSTACK]: 'Full-stack',
  [Role.MOBILE]: 'Mobile',
};

function mapJobFuzzyMatches(
  jobMatchesSummaryResult: GraphQLResult<JobMatchesSummaryQuery>,
): JobFuzzyMatch[] {
  return R.map(
    R.pick(['fullMatch', 'role', 'level', 'titleType', 'skills', 'reasonCode', 'missingSkills']),
    jobMatchesSummaryResult.data?.jobMatchesSummary || [],
  );
}

export function getJobFuzzyMatches() {
  return callGraphQL<JobMatchesSummaryQuery>(
    jobMatchesSummary,
  ).then(mapJobFuzzyMatches);
}

export function mapOpenPosition(position: PotentialOpenPositionMatchesQuery['potentialOpenPositionMatches'][0]) {
  const newPosition = {
    ...R.pick(
      ['id', 'name', 'company', 'role', 'active', 'experience'],
      position,
    ),
    skillIds: R.map(
      (skill) => skill.skillId,
      R.reject(
        R.isNil,
        position.skills?.items || [],
      ) as NonNullable<NonNullable<NonNullable<(typeof position)['skills']>['items']>[0]>[],
    ),
  };
  return newPosition;
}

function mapPotentialOpenPositionMatches(
  result: GraphQLResult<PotentialOpenPositionMatchesQuery>,
): OpenPosition[] {
  return R.map(
    mapOpenPosition,
    result.data?.potentialOpenPositionMatches || [],
  );
}

export function getPotentialOpenPositionMatches() {
  return callGraphQL<PotentialOpenPositionMatchesQuery>(potentialOpenPositionMatches)
    .then(mapPotentialOpenPositionMatches);
}
